import React from 'react';
import { Download, Package, Code, Server, FileText } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Downloads = () => {
  const downloads = [
    {
      id: 1,
      title: 'Package Complet',
      icon: Package,
      description: 'Contient les 3 ZIPs + instructions détaillées de déploiement',
      size: '823 KB',
      file: '/downloads/IntegralTech-TOUS-LES-FICHIERS.zip',
      recommended: true,
      color: 'from-teal-500 to-cyan-500'
    },
    {
      id: 2,
      title: 'Frontend uniquement',
      icon: Code,
      description: 'Fichiers statiques prêts pour FTP (HTML, CSS, JS). Compatible hébergement web classique.',
      size: '531 KB',
      file: '/downloads/integraltech-frontend.zip',
      color: 'from-blue-500 to-indigo-500'
    },
    {
      id: 3,
      title: 'Backend uniquement',
      icon: Server,
      description: 'Code source Python FastAPI. Nécessite hébergement Python.',
      size: '1.9 KB',
      file: '/downloads/integraltech-backend.zip',
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 4,
      title: 'Projet complet (Source)',
      icon: FileText,
      description: 'Code source complet Frontend + Backend. Pour développement et personnalisation.',
      size: '287 KB',
      file: '/downloads/integraltech-complet.zip',
      color: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 space-y-4 animate-in fade-in duration-700">
          <h1 className="text-5xl md:text-6xl font-bold text-white">
            Téléchargements <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">IntegralTech</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Tous vos fichiers prêts pour le déploiement FTP
          </p>
        </div>

        {/* Downloads Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {downloads.map((item) => {
            const Icon = item.icon;
            return (
              <Card 
                key={item.id}
                className={`bg-slate-900 border-slate-800 hover:border-teal-500/50 transition-all duration-300 group relative overflow-hidden ${
                  item.recommended ? 'ring-2 ring-teal-500' : ''
                }`}
              >
                {item.recommended && (
                  <div className="absolute top-4 right-4 z-10">
                    <span className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-xs px-3 py-1 rounded-full font-semibold">
                      Recommandé
                    </span>
                  </div>
                )}
                <CardContent className="p-8">
                  <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-400 mb-4 leading-relaxed">{item.description}</p>
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-teal-400 font-semibold">{item.size}</span>
                  </div>
                  <a href={item.file} download>
                    <Button className={`w-full bg-gradient-to-r ${item.color} hover:opacity-90 text-white py-6 text-lg font-medium shadow-lg transition-all duration-300 group/btn`}>
                      <Download className="mr-2 group-hover/btn:animate-bounce" size={20} />
                      Télécharger
                    </Button>
                  </a>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Instructions Card */}
        <Card className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/20">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <FileText className="mr-3 text-yellow-400" size={28} />
              Instructions de déploiement
            </h2>
            
            <div className="space-y-6 text-gray-300">
              <div>
                <h3 className="text-xl font-semibold text-yellow-400 mb-3">📋 Pour FTP classique :</h3>
                <ol className="list-decimal list-inside space-y-2 ml-4">
                  <li>Téléchargez <strong className="text-teal-400">"Frontend uniquement"</strong></li>
                  <li>Décompressez le fichier ZIP sur votre ordinateur</li>
                  <li>Connectez-vous à votre hébergement via FTP</li>
                  <li>Uploadez <strong>TOUS</strong> les fichiers à la racine de votre site :
                    <ul className="list-disc list-inside ml-6 mt-2 text-gray-400">
                      <li>index.html</li>
                      <li>dossier /static (complet)</li>
                      <li>asset-manifest.json</li>
                    </ul>
                  </li>
                  <li>Visitez votre site : http://integraltech.site</li>
                </ol>
              </div>

              <div className="border-t border-yellow-500/20 pt-6">
                <h3 className="text-xl font-semibold text-yellow-400 mb-3">⚠️ Important :</h3>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">•</span>
                    <span>Le frontend seul contient des <strong>données MOCKÉES</strong> (simulées)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">•</span>
                    <span>Le chatbot et les formulaires sont des fonctionnalités de démonstration</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">•</span>
                    <span>Pour un site complet avec backend fonctionnel, vous aurez besoin d'un hébergement Python</span>
                  </li>
                </ul>
              </div>

              <div className="border-t border-yellow-500/20 pt-6">
                <h3 className="text-xl font-semibold text-teal-400 mb-3">🚀 Pour un déploiement complet :</h3>
                <p className="text-gray-400">
                  Si vous souhaitez activer toutes les fonctionnalités (formulaires réels, chatbot IA, base de données), 
                  contactez-moi pour finaliser le backend ou utilisez la plateforme Emergent pour un déploiement en 1 clic.
                </p>
              </div>
            </div>

            <div className="mt-8">
              <a href="/downloads/INSTRUCTIONS_DEPLOIEMENT.txt" download>
                <Button className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white">
                  <FileText className="mr-2" size={18} />
                  Télécharger les instructions complètes (TXT)
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Downloads;
